import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import MarkdownRenderer from './MarkdownRenderer.jsx';
import { AttachmentChip } from '../AttachmentChip.jsx';
import { ImagePreviewModal } from '../ImagePreviewModal.jsx';

const UserPart = ({ message, isFocused = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = useCallback((file) => {
    setSelectedImage(file);
    setShowModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setSelectedImage(null);
    setShowModal(false);
  }, []);

  const filesElements = useMemo(() => {
    const hasFiles = (message.files?.length || message.metadata?.images?.length);
    if (!hasFiles) return null;

    const files = message.files?.length
      ? message.files
      : message.metadata.images.map((i) => ({ content: i.url }));

    return (
      <div className="flex flex-wrap gap-x-spacing-2 gap-y-spacing-1 mb-spacing-2">
        {files.map((file, i) => (
          <AttachmentChip
            key={i}
            type={file.content?.startsWith('data:image/') ? 'image' : 'file'}
            name={file.name || `File ${i + 1}`}
            url={file.content}
            onPreview={file.content?.startsWith('data:image/') ? () => handleImageClick(file) : undefined}
            context="user"
          />
        ))}
      </div>
    );
  }, [message.files, message.metadata?.images, handleImageClick]);

  const bubbleStyle = `bg-bg-message-user text-primary rounded-lg py-spacing-3 w-full max-w-[692px] break-words min-w-0 mb-spacing-4 text-dls-body ${isFocused ? 'border-r-2 border-accent pr-spacing-3 pl-spacing-4' : 'px-spacing-4'}`;

  return (
    <>
      {filesElements}
      <div className={bubbleStyle}>
        <MarkdownRenderer content={message.content} />
      </div>
      <ImagePreviewModal
        isOpen={showModal}
        onClose={handleModalClose}
        src={selectedImage?.content}
        alt={selectedImage?.name || 'Full size image'}
      />
    </>
  );
};

UserPart.propTypes = {
  message: PropTypes.shape({
    content: PropTypes.string.isRequired,
    files: PropTypes.array,
    metadata: PropTypes.shape({
      images: PropTypes.array,
    }),
  }).isRequired,
  isFocused: PropTypes.bool,
};

export default UserPart;

